import type { MailboxInfo } from 'owa-client-types';
import { getAllAsyncItemEntries, type AsyncItemEntry } from 'owa-copilot-async-chat';
import { TasksHubSection } from '../utils/TasksHubSection';
import mapStatusToSection from '../utils/mapStatusToSection';

export type EntriesBySection = Record<TasksHubSection, AsyncItemEntry[]>;

function getUpdateTime(entry: AsyncItemEntry): number {
    // Sort entries with no known update time as the most recent (top of the list).
    return entry.updateTimeUtc ?? Number.POSITIVE_INFINITY;
}

/**
 * Returns all async items for an account grouped into the Tasks Hub sections, each sorted most-recent first.
 */
export default function getEntriesBySection(mailboxInfo: MailboxInfo): EntriesBySection {
    const sections: EntriesBySection = {
        [TasksHubSection.NeedsAttention]: [],
        [TasksHubSection.InProgress]: [],
        [TasksHubSection.ReadyToReview]: [],
    };

    for (const entry of getAllAsyncItemEntries(mailboxInfo)) {
        const sectionForEntry = mapStatusToSection(entry.status);
        // Drop entries whose status maps to no section (e.g. None/Paused/Failed/Canceled).
        if (sectionForEntry === undefined) {
            continue;
        }
        // Ready to review only shows unread items; drop ones the user has already read.
        if (sectionForEntry === TasksHubSection.ReadyToReview && !entry.isUnread) {
            continue;
        }
        sections[sectionForEntry].push(entry);
    }

    const byNewest = (a: AsyncItemEntry, b: AsyncItemEntry): number => {
        const timeA = getUpdateTime(a);
        const timeB = getUpdateTime(b);
        return timeA === timeB ? 0 : timeB - timeA;
    };
    sections[TasksHubSection.NeedsAttention].sort(byNewest);
    sections[TasksHubSection.InProgress].sort(byNewest);
    sections[TasksHubSection.ReadyToReview].sort(byNewest);

    return sections;
}
